import mondaySdk from 'monday-sdk-js';
import { sanitize, sanitizeNullable } from '../utils/sanitize';

const monday = mondaySdk();

// ─── Complexity budget retry wrapper ──────────────────────────────────────────

interface ComplexityError {
  message: string;
  extensions?: { code?: string; retry_in_seconds?: number };
}

/**
 * Wrap monday.api calls with automatic retry on COMPLEXITY_BUDGET_EXHAUSTED.
 * monday.com's GraphQL API has a shared complexity budget per account that
 * regenerates over time. When exhausted, the API returns `retry_in_seconds`.
 */
async function mondayApiWithRetry<T = unknown>(
  query: string,
  options?: { variables?: Record<string, unknown> },
  maxRetries = 3,
): Promise<{ data: T }> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await monday.api(query, options);

    // monday SDK may return errors at the top level or nested
    const errors: ComplexityError[] =
      (response as { errors?: ComplexityError[] }).errors ?? [];
    const complexityError = errors.find(
      (e) => e.extensions?.code === 'COMPLEXITY_BUDGET_EXHAUSTED',
    );

    if (!complexityError) {
      return response as { data: T };
    }

    if (attempt === maxRetries) {
      throw new Error(
        `monday.com complexity budget exhausted after ${maxRetries + 1} attempts. ` +
        'Try selecting fewer boards or wait a moment before retrying.',
      );
    }

    const waitSeconds = complexityError.extensions?.retry_in_seconds ?? 10;
    await new Promise((resolve) => setTimeout(resolve, waitSeconds * 1000));
  }

  // Unreachable, but satisfies TypeScript
  throw new Error('Unexpected retry loop exit');
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MondayUser {
  id: number;
  name: string;
  email: string;
  photo_thumb_small: string | null;
  title: string | null;
  teams: Array<{ id: number; name: string }>;
}

export interface MondayItemColumnValue {
  id: string;
  type: string;
  text: string;
  value: string | null;
}

export interface MondayItem {
  id: string;
  name: string;
  group: { id: string; title: string; color: string };
  column_values: MondayItemColumnValue[];
  subitems?: MondayItem[];
  parent_item?: { id: string; name?: string; group?: { id: string; title: string; color: string } };
}

// ─── Sanitization helpers ─────────────────────────────────────────────────────

/** Sanitize all user-facing string fields on a MondayItem (and its subitems). */
function sanitizeItem(item: MondayItem): MondayItem {
  return {
    ...item,
    name: sanitize(item.name),
    group: item.group
      ? { ...item.group, title: sanitize(item.group.title) }
      : { id: '', title: '', color: '' },
    column_values: item.column_values.map((cv) => ({
      ...cv,
      text: sanitize(cv.text),
    })),
    subitems: item.subitems?.map(sanitizeItem),
  };
}

/** Sanitize an array of MondayItems. */
function sanitizeItems(items: MondayItem[]): MondayItem[] {
  return items.map(sanitizeItem);
}

// ─── GraphQL queries ──────────────────────────────────────────────────────────

const USERS_PAGE_QUERY = `
  query GetUsers($limit: Int!, $page: Int!) {
    users(kind: non_guests, limit: $limit, page: $page) {
      id
      name
      email
      photo_thumb_small
      title
      teams {
        id
        name
      }
    }
  }
`;

// ─── Item fragments (with / without subitems) ────────────────────────────────

/** Serialize a list of column IDs as a GraphQL array literal, e.g. `["col_1", "col_2"]`. */
function serializeColumnIds(columnIds: readonly string[]): string {
  return `[${columnIds.map((id) => `"${id}"`).join(', ')}]`;
}

/**
 * Build the inline item field selection set.
 *
 * When `columnIds` is a non-empty array, only those columns are fetched via
 * `column_values(ids: [...])` — a major complexity reduction for boards with
 * many columns. When null/undefined/empty, fetches all columns (preserved
 * fallback for callers that don't have a field mapping at hand).
 */
function buildItemFields(columnIds?: readonly string[] | null): string {
  const columnSelector = columnIds && columnIds.length > 0
    ? `column_values(ids: ${serializeColumnIds(columnIds)})`
    : `column_values`;
  return `
    id
    name
    group { id title color }
    parent_item { id name group { id title color } }
    ${columnSelector} {
      id
      type
      text
      value
    }
  `;
}

/** Build the subitems selection set with optional column filtering. */
function buildSubitemFields(columnIds?: readonly string[] | null): string {
  const columnSelector = columnIds && columnIds.length > 0
    ? `column_values(ids: ${serializeColumnIds(columnIds)})`
    : `column_values`;
  return `
    subitems {
      id
      name
      ${columnSelector} {
        id
        type
        text
        value
      }
    }
  `;
}

/** Extract the non-null column IDs referenced by a FieldMapping, suitable for column_values(ids: [...]). */
export function collectMappedColumnIds(
  mapping: {
    timelineColumnId?: string | null;
    assigneesColumnId?: string | null;
    statusColumnId?: string | null;
    colorColumnId?: string | null;
    scheduledHoursColumnId?: string | null;
    scheduledHoursPerDayColumnId?: string | null;
  } | null | undefined,
): string[] {
  if (!mapping) return [];
  const ids = [
    mapping.timelineColumnId,
    mapping.assigneesColumnId,
    mapping.statusColumnId,
    mapping.colorColumnId,
    mapping.scheduledHoursColumnId,
    mapping.scheduledHoursPerDayColumnId,
  ];
  return ids.filter((id): id is string => !!id);
}

function buildBoardItemsPageQuery(
  includeSubitems: boolean,
  columnIds?: readonly string[] | null,
  subitemColumnIds?: readonly string[] | null,
): string {
  return `
    query GetBoardItems($boardId: [ID!]!, $limit: Int!) {
      boards(ids: $boardId) {
        name
        items_page(limit: $limit) {
          cursor
          items {
            ${buildItemFields(columnIds)}
            ${includeSubitems ? buildSubitemFields(subitemColumnIds) : ''}
          }
        }
      }
    }
  `;
}

function buildNextItemsPageQuery(
  includeSubitems: boolean,
  columnIds?: readonly string[] | null,
  subitemColumnIds?: readonly string[] | null,
): string {
  return `
    query GetNextItems($cursor: String!, $limit: Int!) {
      next_items_page(cursor: $cursor, limit: $limit) {
        cursor
        items {
          ${buildItemFields(columnIds)}
          ${includeSubitems ? buildSubitemFields(subitemColumnIds) : ''}
        }
      }
    }
  `;
}

// Pre-built queries for the unfiltered-column case (callers that don't pass
// columnIds still hit these). Filtered cases build the query per-call.
const BOARD_ITEMS_PAGE_QUERY = buildBoardItemsPageQuery(true);
const NEXT_ITEMS_PAGE_QUERY = buildNextItemsPageQuery(true);
const BOARD_ITEMS_PAGE_QUERY_NO_SUBS = buildBoardItemsPageQuery(false);
const NEXT_ITEMS_PAGE_QUERY_NO_SUBS = buildNextItemsPageQuery(false);

// ─── API functions ────────────────────────────────────────────────────────────

/** In-flight deduplication + short cache for fetchMondayUsers */
let _usersCache: MondayUser[] | null = null;
let _usersCacheTimestamp = 0;
let _usersFetchPromise: Promise<MondayUser[]> | null = null;
const USERS_CACHE_TTL_MS = 60_000; // 1 minute

/**
 * Fetch all non-guest users from the monday.com account.
 * Paginates through all pages (monday.com uses 1-based page numbers for users).
 * Deduplicates concurrent calls and caches for 1 minute.
 */
export async function fetchMondayUsers(): Promise<MondayUser[]> {
  // Return cache if valid
  if (_usersCache && Date.now() - _usersCacheTimestamp < USERS_CACHE_TTL_MS) {
    return _usersCache;
  }
  // Deduplicate: if a fetch is in flight, wait for it
  if (_usersFetchPromise) return _usersFetchPromise;

  _usersFetchPromise = _fetchMondayUsersImpl();
  try {
    const result = await _usersFetchPromise;
    _usersCache = result;
    _usersCacheTimestamp = Date.now();
    return result;
  } finally {
    _usersFetchPromise = null;
  }
}

async function _fetchMondayUsersImpl(): Promise<MondayUser[]> {
  const allUsers: MondayUser[] = [];
  const pageLimit = 1000;
  const maxUsers = 2000;
  let page = 1;

  while (allUsers.length < maxUsers) {
    const response = await mondayApiWithRetry(USERS_PAGE_QUERY, {
      variables: { limit: pageLimit, page },
    });

    const data = response.data as {
      users?: Array<{
        id: number | string;
        name: string;
        email: string;
        photo_thumb_small: string | null;
        title: string | null;
        teams?: Array<{ id: number | string; name: string }>;
      }>;
    };

    const users = data.users ?? [];
    if (users.length === 0) break;

    for (const u of users) {
      allUsers.push({
        id: Number(u.id),
        name: sanitize(u.name),
        email: u.email,
        photo_thumb_small: u.photo_thumb_small ?? null,
        title: sanitizeNullable(u.title ?? null),
        teams: (u.teams ?? []).map((t) => ({ id: Number(t.id), name: sanitize(t.name) })),
      });
    }

    if (users.length < pageLimit) break;
    page++;
  }

  return allUsers;
}

/** Cached first day of week from monday.com account settings */
let _firstDayOfWeekCache: 0 | 1 | null = null;
let _firstDayOfWeekPromise: Promise<0 | 1> | null = null;

/**
 * Fetch the account's first day of week setting from monday.com.
 * Cached for the session (doesn't change often). Deduplicates concurrent calls.
 */
export async function fetchFirstDayOfWeek(): Promise<0 | 1> {
  if (_firstDayOfWeekCache !== null) return _firstDayOfWeekCache;
  if (_firstDayOfWeekPromise) return _firstDayOfWeekPromise;

  _firstDayOfWeekPromise = (async () => {
    try {
      const res = await monday.api(
        `query { users (limit: 1) { account { first_day_of_the_week } } }`,
      );
      const acctUsers = (res.data as { users?: { account?: { first_day_of_the_week?: string } }[] })?.users;
      const fdow = acctUsers?.[0]?.account?.first_day_of_the_week;
      const day: 0 | 1 = fdow === 'sunday' ? 0 : 1;
      _firstDayOfWeekCache = day;
      return day;
    } catch {
      return 1; // default Monday
    } finally {
      _firstDayOfWeekPromise = null;
    }
  })();

  return _firstDayOfWeekPromise;
}

/**
 * Fetch all items from a board (unfiltered), handling cursor-based pagination.
 * Internal only — external callers should use `fetchFilteredBoardItems`.
 *
 * When columnIds is provided, complexity is reduced by fetching only the
 * named columns via `column_values(ids: [...])`.
 */
async function fetchAllBoardItems(
  boardId: number,
  pageLimit = 200,
  includeSubitems = true,
  columnIds?: readonly string[] | null,
  subitemColumnIds?: readonly string[] | null,
): Promise<{ boardName: string; items: MondayItem[] }> {
  const allItems: MondayItem[] = [];
  let boardName = '';

  const usesFilteredColumns = (columnIds && columnIds.length > 0)
    || (includeSubitems && subitemColumnIds && subitemColumnIds.length > 0);

  const itemsQuery = usesFilteredColumns
    ? buildBoardItemsPageQuery(includeSubitems, columnIds, subitemColumnIds)
    : (includeSubitems ? BOARD_ITEMS_PAGE_QUERY : BOARD_ITEMS_PAGE_QUERY_NO_SUBS);
  const nextQuery = usesFilteredColumns
    ? buildNextItemsPageQuery(includeSubitems, columnIds, subitemColumnIds)
    : (includeSubitems ? NEXT_ITEMS_PAGE_QUERY : NEXT_ITEMS_PAGE_QUERY_NO_SUBS);

  // First page
  const firstResponse = await mondayApiWithRetry(itemsQuery, {
    variables: { boardId: [String(boardId)], limit: pageLimit },
  });

  const firstData = firstResponse.data as {
    boards?: Array<{
      name: string;
      items_page: {
        cursor: string | null;
        items: MondayItem[];
      };
    }>;
  };

  const board = firstData.boards?.[0];
  if (!board) return { boardName: '', items: [] };

  boardName = sanitize(board.name);
  allItems.push(...board.items_page.items);

  let cursor = board.items_page.cursor;

  // Paginate through remaining pages
  while (cursor) {
    const nextResponse = await mondayApiWithRetry(nextQuery, {
      variables: { cursor, limit: pageLimit },
    });

    const nextData = nextResponse.data as {
      next_items_page: {
        cursor: string | null;
        items: MondayItem[];
      };
    };

    allItems.push(...nextData.next_items_page.items);
    cursor = nextData.next_items_page.cursor;
  }

  return { boardName, items: sanitizeItems(allItems) };
}

// ─── Filtered items fetch ────────────────────────────────────────────────────

/**
 * Server-side filter for `items_page` query_params.
 * All fields are optional — only provided filters are applied (AND-combined).
 * Reusable across timesheets, scheduler, reports, etc.
 */
export interface ItemsFilter {
  /** Timeline column ID + date range for overlap filtering */
  timeline?: {
    columnId: string;
    startDate: string; // ISO date YYYY-MM-DD
    endDate: string;   // ISO date YYYY-MM-DD
  };
  /** People column ID + user IDs to filter by assignee */
  people?: {
    columnId: string;
    userIds: string[]; // monday.com user IDs (numeric strings)
  };
}

interface QueryParamsRule {
  column_id: string;
  compare_value: string | string[];
  compare_attribute?: string;
  operator: string;
}

function buildQueryParams(filter: ItemsFilter): object | null {
  const rules: QueryParamsRule[] = [];

  if (filter.timeline) {
    // Overlap formula: item.start <= window.end AND item.end >= window.start
    rules.push({
      column_id: filter.timeline.columnId,
      compare_value: ['EXACT', filter.timeline.endDate],
      compare_attribute: 'START_DATE',
      operator: 'lower_than_or_equal',
    });
    rules.push({
      column_id: filter.timeline.columnId,
      compare_value: ['EXACT', filter.timeline.startDate],
      compare_attribute: 'END_DATE',
      operator: 'greater_than_or_equals',
    });
  }

  if (filter.people && filter.people.userIds.length > 0) {
    rules.push({
      column_id: filter.people.columnId,
      compare_value: filter.people.userIds.map((id) => `person-${id}`),
      operator: 'any_of',
    });
  }

  if (rules.length === 0) return null;

  return { rules, operator: 'and' };
}

/**
 * Fetch items from a board with server-side filtering via `query_params`.
 * Uses timeline overlap + people column filters to avoid fetching all items.
 *
 * Falls back to unfiltered `fetchAllBoardItems` if no filters are provided.
 * Reusable for timesheets, scheduler, reports, and any feature that needs
 * date-scoped and/or user-scoped board items.
 *
 * @param includeSubitems — When true, fetches the `subitems` field on each item.
 *   Set to true only when itemLevel is 'sub' or 'both' to reduce API payload.
 */
export async function fetchFilteredBoardItems(
  boardId: number,
  filter: ItemsFilter,
  pageLimit = 100,
  includeSubitems = true,
  /** Optional list of column IDs to fetch. When provided, reduces complexity by scoping column_values(ids: [...]). */
  columnIds?: readonly string[] | null,
  /** Optional subitem column IDs (only used when includeSubitems is true). */
  subitemColumnIds?: readonly string[] | null,
): Promise<{ boardName: string; items: MondayItem[] }> {
  const queryParams = buildQueryParams(filter);

  // No filters → fall back to unfiltered fetch
  if (!queryParams) {
    return fetchAllBoardItems(boardId, pageLimit, includeSubitems, columnIds, subitemColumnIds);
  }

  const allItems: MondayItem[] = [];
  let boardName = '';

  // First page — uses query_params (cannot combine with cursor)
  const filteredQuery = `
    query GetFilteredBoardItems($boardId: [ID!]!, $limit: Int!, $queryParams: ItemsQuery!) {
      boards(ids: $boardId) {
        name
        items_page(limit: $limit, query_params: $queryParams) {
          cursor
          items {
            ${buildItemFields(columnIds)}
            ${includeSubitems ? buildSubitemFields(subitemColumnIds) : ''}
          }
        }
      }
    }
  `;

  const firstResponse = await mondayApiWithRetry(filteredQuery, {
    variables: {
      boardId: [String(boardId)],
      limit: pageLimit,
      queryParams: queryParams,
    },
  });

  const firstData = firstResponse.data as {
    boards?: Array<{
      name: string;
      items_page: {
        cursor: string | null;
        items: MondayItem[];
      };
    }>;
  };

  const board = firstData.boards?.[0];
  if (!board) return { boardName: '', items: [] };

  boardName = sanitize(board.name);
  allItems.push(...board.items_page.items);

  // Subsequent pages use next_items_page with cursor. When a column filter is
  // active we must build the follow-up query with the same filter applied —
  // the pre-built constants assume all columns.
  const usesFilteredColumns = (columnIds && columnIds.length > 0)
    || (includeSubitems && subitemColumnIds && subitemColumnIds.length > 0);
  const nextQuery = usesFilteredColumns
    ? buildNextItemsPageQuery(includeSubitems, columnIds, subitemColumnIds)
    : (includeSubitems ? NEXT_ITEMS_PAGE_QUERY : NEXT_ITEMS_PAGE_QUERY_NO_SUBS);
  let cursor = board.items_page.cursor;
  while (cursor) {
    const nextResponse = await mondayApiWithRetry(nextQuery, {
      variables: { cursor, limit: pageLimit },
    });

    const nextData = nextResponse.data as {
      next_items_page: {
        cursor: string | null;
        items: MondayItem[];
      };
    };

    allItems.push(...nextData.next_items_page.items);
    cursor = nextData.next_items_page.cursor;
  }

  return { boardName, items: sanitizeItems(allItems) };
}

/** Server-side filter rules for fetchBoardItemsPage */
export interface ItemsPageFilterRules {
  /** Name search text (uses contains_text operator) */
  searchText?: string;
  /** People column filter: column ID + user IDs */
  assignee?: { columnId: string; userIds: number[] };
  /** Status column filter: column ID + numeric status indices */
  status?: { columnId: string; indices: number[] };
  /** Group filter: group IDs */
  groupIds?: string[];
}

/** Build monday.com query_params from our filter rules */
function buildItemsPageQueryParams(
  filters: ItemsPageFilterRules,
): { rules: Array<Record<string, unknown>>; operator: string } | null {
  const rules: Array<Record<string, unknown>> = [];

  if (filters.searchText) {
    rules.push({
      column_id: 'name',
      compare_value: [filters.searchText],
      operator: 'contains_text',
    });
  }

  if (filters.assignee && filters.assignee.userIds.length > 0) {
    rules.push({
      column_id: filters.assignee.columnId,
      compare_value: filters.assignee.userIds.map((id) => `person-${id}`),
      operator: 'any_of',
    });
  }

  if (filters.status && filters.status.indices.length > 0) {
    rules.push({
      column_id: filters.status.columnId,
      compare_value: filters.status.indices,
    });
  }

  if (filters.groupIds && filters.groupIds.length > 0) {
    rules.push({
      column_id: 'group',
      compare_value: filters.groupIds,
      operator: 'any_of',
    });
  }

  if (rules.length === 0) return null;

  return { rules, operator: 'and' };
}

/**
 * Fetch a single page of items from a board (no automatic pagination).
 * Used by the Item Search panel for initial load and search — only fetches
 * up to `limit` items and returns a cursor for "load more" if available.
 *
 * Supports server-side filtering via query_params:
 * - searchText: name column contains_text
 * - assignee: people column any_of
 * - status: status column any_of
 * Note: group filtering is done client-side (not supported by query_params)
 */
export async function fetchBoardItemsPage(
  boardId: number,
  options: {
    limit?: number;
    /** @deprecated Use filters.searchText instead */
    searchText?: string;
    cursor?: string;
    includeSubitems?: boolean;
    filters?: ItemsPageFilterRules;
  } = {},
): Promise<{ boardName: string; items: MondayItem[]; cursor: string | null }> {
  const { limit = 100, cursor, includeSubitems = true } = options;

  // Merge legacy searchText into filters for backwards compatibility
  const filters: ItemsPageFilterRules = {
    ...options.filters,
    searchText: options.filters?.searchText ?? options.searchText,
  };

  // Subsequent page via cursor
  if (cursor) {
    const nextQuery = includeSubitems ? NEXT_ITEMS_PAGE_QUERY : NEXT_ITEMS_PAGE_QUERY_NO_SUBS;
    const response = await mondayApiWithRetry(nextQuery, {
      variables: { cursor, limit },
    });
    const data = response.data as {
      next_items_page: { cursor: string | null; items: MondayItem[] };
    };
    return {
      boardName: '',
      items: sanitizeItems(data.next_items_page.items),
      cursor: data.next_items_page.cursor,
    };
  }

  // Build query_params from filter rules
  const queryParams = buildItemsPageQueryParams(filters);

  if (queryParams) {
    const filteredQuery = `
      query GetFilteredBoardItems($boardId: [ID!]!, $limit: Int!, $queryParams: ItemsQuery!) {
        boards(ids: $boardId) {
          name
          items_page(limit: $limit, query_params: $queryParams) {
            cursor
            items {
              ${buildItemFields()}
              ${includeSubitems ? buildSubitemFields() : ''}
            }
          }
        }
      }
    `;
    const response = await mondayApiWithRetry(filteredQuery, {
      variables: { boardId: [String(boardId)], limit, queryParams },
    });
    const data = response.data as {
      boards?: Array<{ name: string; items_page: { cursor: string | null; items: MondayItem[] } }>;
    };
    const board = data.boards?.[0];
    return {
      boardName: sanitize(board?.name ?? ''),
      items: sanitizeItems(board?.items_page.items ?? []),
      cursor: board?.items_page.cursor ?? null,
    };
  }

  // No filters — unfiltered first page
  const itemsQuery = includeSubitems ? BOARD_ITEMS_PAGE_QUERY : BOARD_ITEMS_PAGE_QUERY_NO_SUBS;
  const response = await mondayApiWithRetry(itemsQuery, {
    variables: { boardId: [String(boardId)], limit },
  });
  const data = response.data as {
    boards?: Array<{ name: string; items_page: { cursor: string | null; items: MondayItem[] } }>;
  };
  const board = data.boards?.[0];
  return {
    boardName: sanitize(board?.name ?? ''),
    items: sanitizeItems(board?.items_page.items ?? []),
    cursor: board?.items_page.cursor ?? null,
  };
}

/**
 * Fetch items from multiple boards in a single GraphQL call.
 * Only supports name search (all boards share the "name" column).
 * For column-specific filters (assignee, status), use per-board fetchBoardItemsPage calls.
 *
 * Returns items from all boards, each tagged with the board's name and ID.
 */
export async function fetchMultipleBoardItemsPage(
  boardIds: number[],
  options: {
    limit?: number;
    searchText?: string;
    includeSubitems?: boolean;
  } = {},
): Promise<Array<{ boardId: number; boardName: string; items: MondayItem[]; cursor: string | null }>> {
  const { limit = 25, searchText, includeSubitems = true } = options;

  const queryParams = searchText
    ? { rules: [{ column_id: 'name', compare_value: [searchText], operator: 'contains_text' }], operator: 'and' }
    : null;

  const query = queryParams
    ? `
      query GetMultiBoardFilteredItems($boardIds: [ID!]!, $limit: Int!, $queryParams: ItemsQuery!) {
        boards(ids: $boardIds) {
          id
          name
          items_page(limit: $limit, query_params: $queryParams) {
            cursor
            items {
              ${buildItemFields()}
              ${includeSubitems ? buildSubitemFields() : ''}
            }
          }
        }
      }
    `
    : `
      query GetMultiBoardItems($boardIds: [ID!]!, $limit: Int!) {
        boards(ids: $boardIds) {
          id
          name
          items_page(limit: $limit) {
            cursor
            items {
              ${buildItemFields()}
              ${includeSubitems ? buildSubitemFields() : ''}
            }
          }
        }
      }
    `;

  const variables: Record<string, unknown> = {
    boardIds: boardIds.map(String),
    limit,
  };
  if (queryParams) {
    variables.queryParams = queryParams;
  }

  const response = await mondayApiWithRetry(query, { variables });
  const data = response.data as {
    boards?: Array<{
      id: string;
      name: string;
      items_page: { cursor: string | null; items: MondayItem[] };
    }>;
  };

  return (data.boards ?? []).map((board) => ({
    boardId: Number(board.id),
    boardName: sanitize(board.name),
    items: sanitizeItems(board.items_page.items),
    cursor: board.items_page.cursor,
  }));
}

// ─── Board groups ────────────────────────────────────────────────────────────

export interface MondayGroup {
  id: string;
  title: string;
}

/**
 * Fetch groups for a board directly (no need to fetch all items).
 */
export async function fetchBoardGroups(boardId: number): Promise<MondayGroup[]> {
  const query = `
    query GetBoardGroups($boardId: [ID!]!) {
      boards(ids: $boardId) {
        groups { id title }
      }
    }
  `;
  const response = await mondayApiWithRetry(query, {
    variables: { boardId: [String(boardId)] },
  });
  const data = response.data as {
    boards?: Array<{ groups: MondayGroup[] }>;
  };
  return (data.boards?.[0]?.groups ?? []).map((g) => ({
    ...g,
    title: sanitize(g.title),
  }));
}

// ─── Board types & queries ───────────────────────────────────────────────────

export interface MondayBoard {
  id: string;
  name: string;
}

// ─── Targeted board fetch by IDs ─────────────────────────────────────────────

const BOARDS_BY_IDS_QUERY = `
  query GetBoardsByIds($ids: [ID!]!) {
    boards(ids: $ids) {
      id
      name
    }
  }
`;

/**
 * Fetch board names by their IDs.
 * Used for name resolution (e.g., timesheet board labels) where we only
 * need names for a known set of boards, not the full catalog.
 */
export async function fetchBoardsByIds(boardIds: string[]): Promise<MondayBoard[]> {
  if (boardIds.length === 0) return [];
  const response = await mondayApiWithRetry(BOARDS_BY_IDS_QUERY, {
    variables: { ids: boardIds.slice(0, 100) },
  });
  const data = response.data as {
    boards?: Array<{ id: string | number; name: string }>;
  };
  return (data.boards ?? []).map((b) => ({ id: String(b.id), name: sanitize(b.name) }));
}

// ─── Targeted item fetch by IDs ──────────────────────────────────────────────

export interface MondayItemBasic {
  id: string;
  name: string;
}

const ITEMS_BY_IDS_QUERY = `
  query GetItemsByIds($ids: [ID!]!) {
    items(ids: $ids) {
      id
      name
    }
  }
`;

/**
 * Fetch item names by their IDs.
 * Used for name resolution (e.g., worklog item labels).
 */
export async function fetchItemsByIds(itemIds: string[]): Promise<MondayItemBasic[]> {
  if (itemIds.length === 0) return [];
  const response = await mondayApiWithRetry(ITEMS_BY_IDS_QUERY, {
    variables: { ids: itemIds.slice(0, 100) },
  });
  const data = response.data as {
    items?: Array<{ id: string | number; name: string }>;
  };
  return (data.items ?? []).map((i) => ({ id: String(i.id), name: sanitize(i.name) }));
}

// ─── Single item with column values ──────────────────────────────────────────

const ITEM_WITH_COLUMNS_QUERY = `
  query GetItemWithColumns($ids: [ID!]!) {
    items(ids: $ids) {
      id
      name
      board { id }
      column_values {
        id
        type
        text
        value
      }
    }
  }
`;

export interface MondayItemWithColumns {
  id: string;
  name: string;
  boardId: string;
  columnValues: MondayItemColumnValue[];
}

/**
 * Fetch a single item with all its column values.
 * Used to read mapped field values (e.g., original estimate).
 */
export async function fetchItemWithColumns(
  itemId: string,
): Promise<MondayItemWithColumns | null> {
  const response = await mondayApiWithRetry(ITEM_WITH_COLUMNS_QUERY, {
    variables: { ids: [itemId] },
  });
  const data = response.data as {
    items?: Array<{
      id: string | number;
      name: string;
      board?: { id: string | number };
      column_values: MondayItemColumnValue[];
    }>;
  };
  const item = data.items?.[0];
  if (!item) return null;
  return {
    id: String(item.id),
    name: sanitize(item.name),
    boardId: String(item.board?.id ?? ''),
    columnValues: item.column_values,
  };
}

// ─── Enriched item fetch (with board + group context) ────────────────────────

export interface MondayItemWithContext {
  id: string;
  name: string;
  boardName: string;
  groupTitle: string;
}

const ITEMS_WITH_CONTEXT_QUERY = `
  query GetItemsWithContext($ids: [ID!]!) {
    items(ids: $ids) {
      id
      name
      board { name }
      group { title }
    }
  }
`;

/**
 * Fetch items by IDs with board name and group title.
 * Used for enriched worklog display in time tracking views.
 */
export async function fetchItemsWithContextByIds(
  itemIds: string[],
): Promise<MondayItemWithContext[]> {
  if (itemIds.length === 0) return [];
  const response = await mondayApiWithRetry(ITEMS_WITH_CONTEXT_QUERY, {
    variables: { ids: itemIds.slice(0, 100) },
  });
  const data = response.data as {
    items?: Array<{
      id: string | number;
      name: string;
      board?: { name: string };
      group?: { title: string };
    }>;
  };
  return (data.items ?? []).map((i) => ({
    id: String(i.id),
    name: sanitize(i.name),
    boardName: sanitize(i.board?.name ?? ''),
    groupTitle: sanitize(i.group?.title ?? ''),
  }));
}

// ─── Status column settings ──────────────────────────────────────────────────

/**
 * Map from status label index to hex color.
 * Built from a status column's `settings_str`.
 */
export type StatusColorMap = Map<number, string>;

/** Parsed status column settings: label colors and done-state indices. */
export interface StatusColumnInfo {
  /** Map from label index → hex color */
  colorMap: StatusColorMap;
  /** Set of label indices that represent "done" / completed states */
  doneIndices: Set<number>;
}

/**
 * Fetch the column settings cache for all columns on a board.
 * Shared between status color and done-index resolution to avoid
 * duplicate API calls for the same board.
 */
const boardColumnsSettingsCache = new Map<string, Array<{ id: string; type: string; settings_str: string }>>();

async function fetchBoardColumnsSettings(
  boardId: number,
): Promise<Array<{ id: string; type: string; settings_str: string }>> {
  const cacheKey = String(boardId);
  const cached = boardColumnsSettingsCache.get(cacheKey);
  if (cached) return cached;

  const query = `
    query GetColumnSettings($boardId: [ID!]!) {
      boards(ids: $boardId) {
        columns { id type settings_str }
      }
    }
  `;

  const response = await mondayApiWithRetry(query, {
    variables: { boardId: [String(boardId)] },
  });

  const data = response.data as {
    boards?: Array<{
      columns: Array<{ id: string; type: string; settings_str: string }>;
    }>;
  };

  const columns = data.boards?.[0]?.columns ?? [];
  boardColumnsSettingsCache.set(cacheKey, columns);
  return columns;
}

/**
 * Parse status column settings to extract label colors and done indices.
 */
function parseStatusColumnSettings(settingsStr: string): StatusColumnInfo {
  try {
    const settings = JSON.parse(settingsStr) as {
      labels_colors?: Record<string, { color: string; border: string; var_name: string }>;
      done_colors?: number[];
    };
    const colorMap: StatusColorMap = new Map();
    if (settings.labels_colors) {
      for (const [index, value] of Object.entries(settings.labels_colors)) {
        if (value.color) {
          colorMap.set(Number(index), value.color);
        }
      }
    }
    const doneIndices = new Set<number>(settings.done_colors ?? []);
    return { colorMap, doneIndices };
  } catch {
    return { colorMap: new Map(), doneIndices: new Set() };
  }
}

/**
 * Fetch the label→color mapping for a status column on a board.
 * Returns a Map<index, hexColor> that can be used to resolve status colors.
 */
export async function fetchStatusColumnColors(
  boardId: number,
  statusColumnId: string,
): Promise<StatusColorMap> {
  const info = await fetchStatusColumnInfo(boardId, statusColumnId);
  return info.colorMap;
}

/**
 * Fetch full status column info (colors + done indices) for a board.
 * Single API call that returns both label colors and done-state indices.
 */
export async function fetchStatusColumnInfo(
  boardId: number,
  statusColumnId: string,
): Promise<StatusColumnInfo> {
  const columns = await fetchBoardColumnsSettings(boardId);
  const statusCol = columns.find((c) => c.id === statusColumnId);
  if (!statusCol) return { colorMap: new Map(), doneIndices: new Set() };
  return parseStatusColumnSettings(statusCol.settings_str);
}

/**
 * A status option with its monday.com index and display label.
 * The index is needed for server-side query_params filtering.
 */
export interface StatusOption {
  index: string;
  label: string;
}

/**
 * Fetch all defined status options for a status column on a board.
 * Parses the `labels` object from `settings_str` (maps index → label text).
 * Returns an array of StatusOption objects with both index and label.
 */
export async function fetchStatusOptions(
  boardId: number,
  statusColumnId: string,
): Promise<StatusOption[]> {
  const columns = await fetchBoardColumnsSettings(boardId);
  const statusCol = columns.find((c) => c.id === statusColumnId);
  if (!statusCol) return [];

  try {
    const settings = JSON.parse(statusCol.settings_str) as {
      labels?: Record<string, string>;
    };
    if (!settings.labels) return [];
    return Object.entries(settings.labels)
      .filter(([, label]) => label && label.trim() !== '')
      .map(([index, label]) => ({ index, label }));
  } catch {
    return [];
  }
}

/**
 * Fetch all defined status labels for a status column on a board.
 * @deprecated Use fetchStatusOptions instead for proper query_params filtering.
 */
export async function fetchStatusLabels(
  boardId: number,
  statusColumnId: string,
): Promise<string[]> {
  const options = await fetchStatusOptions(boardId, statusColumnId);
  return options.map((o) => o.label);
}

// ─── Subitems board resolution ───────────────────────────────────────────────

export interface SubitemsBoardInfo {
  subitemsBoardId: number;
  /** Column ID of a timeline-type column on the subitems board (if any) */
  timelineColumnId: string | null;
  /** Column ID of a people-type column on the subitems board (if any) */
  peopleColumnId: string | null;
}

const subitemsBoardCache = new Map<number, SubitemsBoardInfo>();

/**
 * Fetch the subitems board ID and its column mapping for a given parent board.
 * monday.com stores subitems on a separate board — we find it via the
 * parent board's "subitems" column `settings_str` which contains `boardIds`.
 * Results are cached per parent board.
 */
export async function fetchSubitemsBoardInfo(
  parentBoardId: number,
): Promise<SubitemsBoardInfo | null> {
  const cached = subitemsBoardCache.get(parentBoardId);
  if (cached) return cached;

  // Step 1: Find the subitems column on the parent board to get the subitems board ID
  const query = `
    query GetSubitemsBoard($boardId: [ID!]!) {
      boards(ids: $boardId) {
        columns {
          id
          type
          settings_str
        }
      }
    }
  `;

  const response = await mondayApiWithRetry(query, {
    variables: { boardId: [String(parentBoardId)] },
  });

  const data = response.data as {
    boards?: Array<{
      columns: Array<{ id: string; type: string; settings_str: string }>;
    }>;
  };

  const board = data.boards?.[0];
  if (!board) return null;

  const subitemsCol = board.columns.find((c) => c.type === 'subtasks');
  if (!subitemsCol) return null;

  let subitemsBoardId: number | null = null;
  try {
    const settings = JSON.parse(subitemsCol.settings_str) as { boardIds?: number[] };
    subitemsBoardId = settings.boardIds?.[0] ?? null;
  } catch { /* ignore */ }

  if (!subitemsBoardId) return null;

  // Step 2: Fetch columns of the subitems board to find timeline + people columns
  const colQuery = `
    query GetSubitemsBoardColumns($boardId: [ID!]!) {
      boards(ids: $boardId) {
        columns { id type }
      }
    }
  `;

  const colResponse = await mondayApiWithRetry(colQuery, {
    variables: { boardId: [String(subitemsBoardId)] },
  });

  const colData = colResponse.data as {
    boards?: Array<{
      columns: Array<{ id: string; type: string }>;
    }>;
  };

  const subBoard = colData.boards?.[0];
  const columns = subBoard?.columns ?? [];

  const info: SubitemsBoardInfo = {
    subitemsBoardId,
    timelineColumnId: columns.find((c) => c.type === 'timeline')?.id ?? null,
    peopleColumnId: columns.find((c) => c.type === 'people')?.id ?? null,
  };

  subitemsBoardCache.set(parentBoardId, info);
  return info;
}

/**
 * Update a column value on a monday.com item.
 */
export async function updateMondayItem(
  boardId: number,
  itemId: number,
  columnId: string,
  value: string,
): Promise<void> {
  const mutation = `
    mutation UpdateColumnValue($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(board_id: $boardId, item_id: $itemId, column_id: $columnId, value: $value) {
        id
      }
    }
  `;

  await mondayApiWithRetry(mutation, {
    variables: {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId,
      value,
    },
  });
}
